import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Sparkles, Stethoscope, ShieldCheck, ArrowRight, Info, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function KidIdHelper() {
  const [school, setSchool] = useState("DAV");
  const [loc, setLoc] = useState("PAL");
  const [reg, setReg] = useState("PL11493");
  const [result, setResult] = useState(null);
  const check = async () => {
    try {
      const { data } = await http.post("/auth/lookup-kid-id", { school_code: school, location_code: loc, reg_id: reg });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
    }
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="kid-id-info-btn" type="button" className="ml-1 text-slate-400 hover:text-sky-600 transition-colors">
          <Info className="w-4 h-4" aria-label="How to find KID ID" />
        </button>
      </DialogTrigger>
      <DialogContent data-testid="kid-id-helper-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Find your KID ID</DialogTitle>
          <DialogDescription>
            Format: <span className="font-semibold text-slate-800">SCHOOL.LOCATION.REG-ID</span> · e.g., DAV.PAL.PL11493
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest">School</Label>
            <Input data-testid="helper-school-input" value={school} onChange={(e) => setSchool(e.target.value)} placeholder="DAV" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Location</Label>
            <Input data-testid="helper-location-input" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="PAL" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Reg #</Label>
            <Input data-testid="helper-reg-input" value={reg} onChange={(e) => setReg(e.target.value)} placeholder="PL11493" />
          </div>
        </div>
        <Button data-testid="helper-check-btn" onClick={check} className="w-full">Check KID ID</Button>
        {result && (
          <div className="text-sm rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="font-mono text-sky-700 font-bold" data-testid="helper-kid-id-result">{result.kid_id}</div>
            <div className="text-xs text-slate-500 mt-1">
              {result.exists ? `Found: ${result.student_name}` : "No student found yet with this ID"}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [role, setRole] = useState("parent");
  const [kidId, setKidId] = useState("");
  const [email, setEmail] = useState("");
  const [challengeId, setChallengeId] = useState(null);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [devHint, setDevHint] = useState(null);

  const request = async () => {
    setLoading(true);
    try {
      const payload = role === "parent"
        ? { mode: "parent", kid_id: kidId.trim().toUpperCase() }
        : { mode: "staff", email: email.trim().toLowerCase() };
      const { data } = await http.post("/auth/request-otp", payload);
      setChallengeId(data.challenge_id);
      setDevHint(data.dev_otp || null);
      toast.success(`Code sent to ${data.delivered_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send code");
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setLoading(true);
    try {
      const { data } = await http.post("/auth/verify-otp", { challenge_id: challengeId, code: otp });
      login(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      if (data.user.role === "parent") nav("/parent");
      else if (data.user.role === "teacher") nav("/teacher");
      else if (data.user.role === "school_admin") nav("/school-admin");
      else nav("/admin");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 bg-grain flex relative">
      {/* Top-right: About KOI link — visible on login even before sign in. */}
      <a
        data-testid="login-about-koi"
        href="https://agreeable-stone-097ad4d00.3.azurestaticapps.net/"
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-5 right-5 z-20 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold text-sky-700 hover:text-sky-900 bg-white/90 backdrop-blur border border-sky-200 hover:border-sky-400 rounded-full px-3.5 py-2 shadow-sm transition-colors"
      >
        About KOI <ExternalLink className="w-3 h-3" />
      </a>
      <div className="hidden lg:flex flex-col justify-between w-[46%] p-12 relative overflow-hidden bg-gradient-to-br from-sky-500 via-sky-600 to-indigo-600 text-white">
        <div>
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-2xl p-3 shadow-xl">
              <img src="/brand/koi-logo.png" alt="KOI The School Doctor" className="h-24 w-auto" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] opacity-90">The School Doctor</div>
              <div className="font-heading font-bold text-base opacity-95">Preventive Healthcare</div>
            </div>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading font-bold text-4xl xl:text-5xl leading-tight mt-16 max-w-md"
          >
            India&rsquo;s first Health Report Card for kids.
          </motion.h2>
          <p className="mt-4 text-white/85 max-w-md">
            22 WHO-aligned health parameters, screened at school and gamified for lifelong wellness habits.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[
            { icon: <ShieldCheck className="w-4 h-4" />, k: "WHO", v: "Aligned" },
            { icon: <Sparkles className="w-4 h-4" />, k: "8 Levels", v: "Gamified" },
            { icon: <Stethoscope className="w-4 h-4" />, k: "2 Terms", v: "Per Year" },
          ].map((c) => (
            <div key={c.k} className="rounded-2xl bg-white/10 backdrop-blur px-4 py-3">
              <div className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-1">{c.icon}{c.k}</div>
              <div className="font-heading font-semibold text-lg mt-1">{c.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-8 py-12">
        <div className="w-full max-w-md">
          <div className="mb-6 lg:hidden flex items-center gap-3">
            <img src="/brand/koi-logo.png" alt="KOI The School Doctor" className="h-16 w-auto" />
            <div>
              <div className="text-[11px] text-sky-600 tracking-widest uppercase font-bold">The School Doctor</div>
              <div className="text-[11px] text-slate-500 tracking-widest uppercase mt-0.5">Health Report Card</div>
            </div>
          </div>
          <div className="text-xs uppercase tracking-[0.22em] text-sky-600 font-bold">Sign in</div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl mt-2 text-slate-900">Welcome back.</h1>
          <p className="text-slate-500 mt-2">Login with a one-time code — no passwords to remember.</p>

          <div className="mt-8 bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
            <Tabs value={role} onValueChange={(v) => { setRole(v); setChallengeId(null); setOtp(""); }}>
              <TabsList className="grid grid-cols-2 w-full" data-testid="role-tabs">
                <TabsTrigger data-testid="tab-parent" value="parent">Parent</TabsTrigger>
                <TabsTrigger data-testid="tab-staff" value="staff">Teacher / Admin</TabsTrigger>
              </TabsList>

              <TabsContent value="parent" className="mt-5">
                {!challengeId ? (
                  <>
                    <Label className="text-xs uppercase tracking-widest text-slate-500 flex items-center">
                      KID ID <KidIdHelper />
                    </Label>
                    <Input
                      data-testid="parent-kid-id-input"
                      placeholder="e.g. DAV.PAL.PL11493"
                      value={kidId}
                      onChange={(e) => setKidId(e.target.value)}
                      className="mt-2 font-mono tracking-wider uppercase"
                    />
                    <div className="text-[11px] text-slate-500 mt-2">Format: SCHOOL.LOCATION.REGID</div>
                    <Button
                      data-testid="parent-request-otp-btn"
                      onClick={request}
                      disabled={!kidId || loading}
                      className="mt-5 w-full h-11 rounded-xl bg-sky-500 hover:bg-sky-600"
                    >
                      Send code <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                    <div className="text-[11px] text-slate-400 mt-4 text-center">
                      Try demo: <span className="font-mono text-slate-600">DAV.PAL.PL11493</span>
                    </div>
                  </>
                ) : (
                  <OtpForm otp={otp} setOtp={setOtp} verify={verify} loading={loading} devHint={devHint} onReset={() => setChallengeId(null)} />
                )}
              </TabsContent>

              <TabsContent value="staff" className="mt-5">
                {!challengeId ? (
                  <>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Email</Label>
                    <Input
                      data-testid="staff-email-input"
                      placeholder="you@school.edu"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-2"
                    />
                    <Button
                      data-testid="staff-request-otp-btn"
                      onClick={request}
                      disabled={!email || loading}
                      className="mt-5 w-full h-11 rounded-xl bg-sky-500 hover:bg-sky-600"
                    >
                      Send code <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                    <div className="text-[11px] text-slate-400 mt-4 text-center">
                      Demo teacher: <span className="font-mono text-slate-600">teacher@koi.demo</span> · admin: <span className="font-mono text-slate-600">admin@koi.demo</span>
                    </div>
                  </>
                ) : (
                  <OtpForm otp={otp} setOtp={setOtp} verify={verify} loading={loading} devHint={devHint} onReset={() => setChallengeId(null)} />
                )}
              </TabsContent>
            </Tabs>
          </div>

          <div className="mt-6 text-[11px] text-slate-400 text-center">
            By continuing you agree to KOI&rsquo;s terms and privacy policy.
          </div>
        </div>
      </div>
    </div>
  );
}

function OtpForm({ otp, setOtp, verify, loading, devHint, onReset }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500">Enter 6-digit code</Label>
      <div className="mt-3 flex justify-center">
        <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg" />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>
      {devHint && (
        <div data-testid="dev-otp-hint" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-center">
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Dev mode &mdash; use code</div>
          <div className="font-mono font-extrabold text-lg text-amber-900 tracking-[0.4em] mt-0.5">{devHint}</div>
        </div>
      )}
      <Button
        data-testid="verify-otp-btn"
        onClick={verify}
        disabled={otp.length !== 6 || loading}
        className="mt-5 w-full h-11 rounded-xl bg-sky-500 hover:bg-sky-600"
      >
        Verify & sign in
      </Button>
      <button
        data-testid="otp-reset-btn"
        type="button"
        onClick={onReset}
        className="w-full mt-3 text-xs text-slate-500 hover:text-slate-800"
      >
        Wrong ID/email? Go back
      </button>
    </div>
  );
}
